import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, TrendingUp, Phone, CalendarCheck, AlertCircle,
    Users, ChevronRight, Mountain,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";

const GOLD = "#C9A84C";
const NAVY = "#0A2540";
const COBALT = "#1E6FD9";

const fmtCents = (c) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100);

export default function MyPipeline() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [upline, setUpline] = useState(null);
    const [showChain, setShowChain] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([adminApi.myDashboard(), adminApi.myUpline().catch(() => null)])
            .then(([d, u]) => { setData(d); setUpline(u); })
            .catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load your dashboard."))
            .finally(() => setLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);

    const maxFunnel = useMemo(() => {
        if (!data?.funnel?.length) return 0;
        return Math.max(...data.funnel.map((s) => s.count || 0), 1);
    }, [data]);

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center">
                <Loader2 className="animate-spin text-mm-navy" size={32} />
            </div>
        );
    }
    if (!data) return null;

    const g = data.greeting || {};

    return (
        <div className="space-y-6" data-testid="my-pipeline">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="mm-eyebrow flex items-center gap-1.5"><Mountain size={11} /> My Pipeline</div>
                    <h1 className="mm-display text-3xl text-mm-navy">
                        Good {g.time_of_day}, {g.first_name}.
                    </h1>
                    <p className="mt-1 text-sm text-mm-muted">
                        {g.role === "agent" ? "Here's your day." : `Personal view (${g.role?.replace("_", " ")}).`}
                        {g.last_login_at && <> Last login {new Date(g.last_login_at).toLocaleString()}.</>}
                    </p>
                </div>
            </header>

            {/* Phase 9 — Upline card */}
            <UplineCard upline={upline} showChain={showChain} onToggleChain={() => setShowChain((s) => !s)} />

            {/* Quick action row */}
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="my-quick-actions">
                {(data.quick_actions || []).map((qa) => (
                    <Link
                        key={qa.label}
                        to={qa.href}
                        className="mm-card flex items-center justify-between p-3 transition-colors hover:bg-mm-bg/40"
                        data-testid={`my-qa-${slugify(qa.label)}`}
                    >
                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-mm-muted">Quick action</div>
                            <div className="mt-0.5 text-sm font-semibold text-mm-navy">{qa.label}</div>
                        </div>
                        <ChevronRight size={14} className="text-mm-muted" />
                    </Link>
                ))}
            </section>

            {/* Top row: YTD production + Open alerts */}
            <section className="grid gap-4 md:grid-cols-3">
                <div className="mm-card col-span-2 p-5" data-testid="my-production-ytd">
                    <div className="mb-2 flex items-center gap-1.5">
                        <TrendingUp size={11} style={{ color: COBALT }} />
                        <div className="mm-eyebrow" style={{ color: COBALT }}>My YTD Production</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Stat label="Policies closed" value={(data.scorecard_ytd?.policies_closed ?? 0).toString()} />
                        <Stat label="Annual premium" value={fmtCents(data.scorecard_ytd?.total_annual_premium_cents)} />
                    </div>
                </div>

                <div className="mm-card p-5" data-testid="my-open-alerts">
                    <div className="mb-2 flex items-center gap-1.5">
                        <AlertCircle size={11} className="text-rose-700" />
                        <div className="mm-eyebrow text-rose-700">Open alerts</div>
                    </div>
                    <div className="mm-display text-4xl text-mm-navy">{data.open_alerts?.count ?? 0}</div>
                    <p className="mt-1 text-xs text-mm-muted">Missed-draft alerts on your closed policies.</p>
                    <Link to="/admin/policies?payment_status=missed" className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-mm-navy hover:underline">
                        Review alerts <ChevronRight size={12} />
                    </Link>
                </div>
            </section>

            {/* Funnel */}
            <section className="mm-card p-5" data-testid="my-funnel">
                <div className="mb-3 flex items-center gap-1.5">
                    <TrendingUp size={11} />
                    <div className="mm-eyebrow">My pipeline</div>
                </div>
                <div className="space-y-1.5">
                    {(data.funnel || []).map((s, i) => {
                        const width = Math.max(4, Math.round((s.count / maxFunnel) * 100));
                        const tone = i === data.funnel.length - 1 ? GOLD : i < data.funnel.length / 2 ? COBALT : NAVY;
                        return (
                            <div key={s.stage} className="grid grid-cols-[150px_1fr_40px] items-center gap-3 text-xs" data-testid={`my-funnel-${i}`}>
                                <div className="text-right font-medium text-mm-navy">{s.stage}</div>
                                <div className="relative h-6 rounded-full bg-mm-bg">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${width}%`, background: tone }} />
                                </div>
                                <div className="text-right text-mm-muted tabular-nums">{s.count}</div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Upcoming calls */}
            <section className="mm-card p-5" data-testid="my-upcoming">
                <div className="mb-3 flex items-center gap-1.5">
                    <CalendarCheck size={11} />
                    <div className="mm-eyebrow">Next 3 booked calls</div>
                </div>
                {(data.upcoming_calls || []).length === 0 ? (
                    <div className="text-sm text-mm-muted">
                        Nothing booked yet. <Link to="/admin/bookings" className="text-mm-navy hover:underline">Open bookings</Link>.
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {data.upcoming_calls.map((b) => (
                            <li key={b.id} className="flex items-center justify-between rounded-md border border-mm-line bg-white p-2.5" data-testid={`my-call-${b.id}`}>
                                <div>
                                    <div className="text-sm font-medium text-mm-navy">{b.name || "Booked call"}</div>
                                    <div className="text-[11px] text-mm-muted">
                                        {b.confirmed_slot || b.preferred_date || "Date TBD"}
                                        {b.preferred_window && <span> · {b.preferred_window}</span>}
                                    </div>
                                </div>
                                <Link to={b.lead_id ? `/admin/leads?focus=${b.lead_id}` : "/admin/bookings"} className="inline-flex items-center gap-1 text-xs font-medium text-mm-navy hover:underline">
                                    Open <ChevronRight size={12} />
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}

function Stat({ label, value, sub, accent }) {
    return (
        <div className="rounded-md border border-mm-line bg-white p-3">
            <div className="text-[10px] uppercase tracking-wider text-mm-muted">{label}</div>
            <div className="mm-display mt-1 text-xl tabular-nums" style={{ color: accent || NAVY }}>{value}</div>
            {sub && <div className="text-[11px] text-mm-muted mt-0.5">{sub}</div>}
        </div>
    );
}

function slugify(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }

function initials(first, last) {
    return ((first || "").slice(0, 1) + (last || "").slice(0, 1)).toUpperCase() || "MM";
}

function UplineCard({ upline, showChain, onToggleChain }) {
    if (!upline) return null;
    if (upline.at_top || !upline.upline) {
        return (
            <div className="mm-card flex items-center gap-3 p-4" data-testid="my-upline-top">
                <div className="flex h-10 w-10 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: NAVY }}>
                    <Mountain size={16} />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-mm-muted">Your position</div>
                    <div className="text-sm font-semibold text-mm-navy">You're at the top of your reporting chain.</div>
                </div>
            </div>
        );
    }
    const u = upline.upline;
    const hasChain = Array.isArray(upline.chain) && upline.chain.length > 1;
    return (
        <div className="mm-card p-4" data-testid="my-upline-card">
            <div className="flex items-center gap-3">
                <div
                    className="flex h-11 w-11 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ background: `linear-gradient(135deg, ${NAVY}, ${COBALT})` }}
                    data-testid="my-upline-avatar"
                >
                    {initials(u.first_name, u.last_name)}
                </div>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-mm-muted">You report to</div>
                    <div className="text-sm font-semibold text-mm-navy" data-testid="my-upline-name">
                        {u.first_name} {u.last_name} <span className="text-mm-muted font-normal">· {(u.role || "").replace("_", " ")}</span>
                    </div>
                </div>
                {hasChain && (
                    <button
                        type="button"
                        onClick={onToggleChain}
                        className="text-xs font-medium text-mm-navy hover:underline"
                        data-testid="my-upline-toggle-chain"
                    >
                        {showChain ? "Hide chain" : "View upline chain"}
                    </button>
                )}
            </div>
            {showChain && hasChain && (
                <ol className="mt-3 space-y-1.5 border-t border-mm-line pt-3" data-testid="my-upline-chain">
                    {upline.chain.map((c, idx) => (
                        <li key={c.id} className="flex items-center gap-2 text-xs">
                            <span className="w-4 text-right text-mm-muted tabular-nums">{idx + 1}.</span>
                            <span className="font-medium text-mm-navy">{c.first_name} {c.last_name}</span>
                            <span className="text-mm-muted">· {(c.role || "").replace("_", " ")}</span>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}
